import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { popupsService } from "../services/api";
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactQuill from 'react-quill-new';
import 'react-quill/dist/quill.snow.css';

const PopupAddPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    content_type: 'content',
    content: '',
    image_url: '',
    image_link: '',
    video_url: '',
    button_text: '',
    button_link: '',
    start_date: '',
    end_date: '',
    is_active: 1,
    cookie_control: 1,
    sort_order: 0
  });

  const [filters, setFilters] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    roles: [],
    plans: [],
    categories: [],
    cities: [],
    companyTypes: []
  });
  const [filterCount, setFilterCount] = useState(0);
  const [statistics, setStatistics] = useState({
    view: 0,
    click: 0,
    close: 0,
    dismiss: 0
  });
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);

  const loadFilterOptions = async () => {
    try {
      setLoadingOptions(true);
      const response = await popupsService.getFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      } else {
        toast.error(response.error || 'Filtre seçenekleri yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
      toast.error('Filtre seçenekleri yüklenirken bir hata oluştu');
    } finally {
      setLoadingOptions(false);
    }
  };

  const loadPopup = useCallback(async () => {
    try {
      setLoading(true);
      const response = await popupsService.getPopup(id);
      if (response.success) {
        const popup = response.data;
        setFormData({
          title: popup.title || '',
          content_type: popup.content_type || 'content',
          content: popup.content || '',
          image_url: popup.image_url || '',
          image_link: popup.image_link || '',
          video_url: popup.video_url || '',
          button_text: popup.button_text || '',
          button_link: popup.button_link || '',
          start_date: popup.start_date ? popup.start_date.split('T')[0] + 'T' + popup.start_date.split('T')[1].substring(0, 5) : '',
          end_date: popup.end_date ? popup.end_date.split('T')[0] + 'T' + popup.end_date.split('T')[1].substring(0, 5) : '',
          is_active: popup.is_active !== undefined ? popup.is_active : 1,
          cookie_control: popup.cookie_control !== undefined ? popup.cookie_control : 1,
          sort_order: popup.sort_order || 0
        });
        setFilters(popup.filters || []);
        setStatistics(popup.statistics || {
          view: 0,
          click: 0,
          close: 0,
          dismiss: 0
        });
      } else {
        toast.error(response.error || 'Popup yüklenemedi');
        navigate('/popups');
      }
    } catch (error) {
      console.error('Error loading popup:', error);
      toast.error('Popup yüklenirken bir hata oluştu');
      navigate('/popups');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const calculateFilterCount = useCallback(async () => {
    try {
      // Sadece filter_value dolu olan filtreleri gönder
      const validFilters = filters.filter(f => f.filter_value && f.filter_value !== '');
      
      if (validFilters.length === 0) {
        setFilterCount(0);
        return;
      }

      console.log('📊 calculateFilterCount called with validFilters:', validFilters);
      const response = await popupsService.calculateFilterCount(validFilters);
      console.log('📊 calculateFilterCount response:', response);
      
      if (response && response.success) {
        setFilterCount(response.count || 0);
      } else {
        console.error('❌ calculateFilterCount error:', response?.error || 'Unknown error');
        setFilterCount(0);
      }
    } catch (error) {
      console.error('Error calculating filter count:', error);
      setFilterCount(0);
    }
  }, [filters]);

  useEffect(() => {
    loadFilterOptions();
    if (isEdit) {
      loadPopup();
    }
  }, [id, isEdit, loadPopup]);

  useEffect(() => {
    // Debounce için kısa bir gecikme ekle
    const timer = setTimeout(() => {
      const validFilters = filters.filter(f => f.filter_value && f.filter_value !== '');
      if (validFilters.length > 0) {
        calculateFilterCount();
      } else {
        setFilterCount(0);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [filters, calculateFilterCount]);

  const addFilter = (filterType) => {
    setFilters(prev => [...prev, { filter_type: filterType, filter_value: '' }]);
  };

  const removeFilter = (index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };

  const updateFilter = (index, field, value) => {
    setFilters(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImageFile(file);
      // Eğer popup ID varsa (edit mode), otomatik yükle
      if (isEdit && id) {
        // Otomatik yükleme yerine buton göster
        // handleImageUploadClick();
      }
    }
  };

  const handleImageUploadClick = async () => {
    if (!selectedImageFile || !isEdit || !id) {
      toast.error('Lütfen önce bir görsel seçin');
      return;
    }

    try {
      setUploadingImage(true);
      const response = await popupsService.uploadImage(id, selectedImageFile);
      
      if (response.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.image_url }));
        setSelectedImageFile(null);
        // File input'u temizle
        const fileInput = document.getElementById('image-upload');
        if (fileInput) {
          fileInput.value = '';
        }
        toast.success('Görsel başarıyla yüklendi');
      } else {
        toast.error(response.error || 'Görsel yüklenemedi');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Görsel yüklenirken bir hata oluştu');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Başlık gereklidir');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error('Başlangıç ve bitiş tarihi gereklidir');
      return;
    }

    if (new Date(formData.start_date) >= new Date(formData.end_date)) {
      toast.error('Bitiş tarihi başlangıç tarihinden sonra olmalıdır');
      return;
    }

    setLoading(true);
    
    // datetime-local input'u local timezone'da verir, bunu Türkiye saati (UTC+3) olarak MySQL formatına çevir
    const formatDateForBackend = (dateTimeLocalString) => {
      if (!dateTimeLocalString) return null;
      
      // datetime-local format: "2025-11-10T14:00"
      // Bunu direkt MySQL datetime formatına çevir (Türkiye saati olarak sakla)
      // Yani kullanıcının seçtiği saat direkt olarak DB'ye yazılır
      const dateTime = dateTimeLocalString.replace('T', ' ');
      return dateTime + ':00'; // MySQL datetime format: "2025-11-10 14:00:00"
    };
    
    const data = {
      ...formData,
      start_date: formatDateForBackend(formData.start_date),
      end_date: formatDateForBackend(formData.end_date),
      filters: filters.filter(f => f.filter_value)
    };
    
    console.log('📅 Popup date data:', {
      original_start: formData.start_date,
      formatted_start: data.start_date,
      original_end: formData.end_date,
      formatted_end: data.end_date
    });

    const response = isEdit
      ? await popupsService.updatePopup(id, data)
      : await popupsService.createPopup(data);
    
    setLoading(false);

    if (response.success) {
      toast.success(isEdit ? 'Popup güncellendi' : 'Popup oluşturuldu');
      
      // Yeni popup oluşturulduysa ve görsel seçilmişse, görseli yükle
      if (!isEdit && response.data && response.data.id && selectedImageFile) {
        const newPopupId = response.data.id;
        try {
          setUploadingImage(true);
          const uploadResponse = await popupsService.uploadImage(newPopupId, selectedImageFile);
          
          if (uploadResponse.success) {
            // Popup'ı görsel URL ile güncelle
            await popupsService.updatePopup(newPopupId, {
              ...formData,
              image_url: uploadResponse.data.image_url
            });
            toast.success('Görsel başarıyla yüklendi');
          }
        } catch (uploadError) {
          console.error('Error uploading image after popup creation:', uploadError);
          toast.warning('Popup oluşturuldu ancak görsel yüklenemedi. Düzenleme sayfasından tekrar deneyebilirsiniz.');
        } finally {
          setUploadingImage(false);
        }
      }
      
      setTimeout(() => {
        navigate('/popups');
      }, isEdit ? 1000 : 2000); // Yeni popup'ta görsel yükleme için daha fazla süre
    } else {
      toast.error(response.error || (isEdit ? 'Popup güncellenemedi' : 'Popup oluşturulamadı'));
    }
  };

  const getFilterValueOptions = (filterType) => {
    switch (filterType) {
      case 'role':
        return filterOptions.roles || [];
      case 'plan':
        return filterOptions.plans || [];
      case 'category':
        return filterOptions.categories || [];
      case 'city':
        return filterOptions.cities || [];
      case 'company_type':
        return filterOptions.companyTypes || [];
      default:
        return [];
    }
  };


  return (
    <MasterLayout>
      <ToastContainer />
      <Breadcrumb title={isEdit ? "Popup Düzenle" : "Popup Ekle"} />
      <div className="dashboard-main active">
        <div className="container-fluid">
          <div className="card">
            <div className="card-header border-0 pb-0 p-24">
              <h5 className="mb-0">{isEdit ? "Popup Düzenle" : "Yeni Popup Ekle"}</h5>
            </div>
            <div className="card-body p-24">
              {loadingOptions ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Basic Info */}
                  <div className="row mb-4">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        Başlık <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Popup başlığı"
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Başlangıç Tarihi <span className="text-danger">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.start_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        Bitiş Tarihi <span className="text-danger">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={formData.end_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                        required
                      />
                    </div>
                  </div>

                  {/* Content Type */}
                  <div className="row mb-4">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        İçerik Tipi <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        value={formData.content_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, content_type: e.target.value }))}
                        required
                      >
                        <option value="content">İçerik</option>
                        <option value="image">Görsel</option>
                        <option value="video">Video</option>
                      </select>
                    </div>
                  </div>

                  {/* Content based on type */}
                  {console.log('🔍 Content type:', formData.content_type)}
                  {formData.content_type === 'content' && (
                    <div className="row mb-4">
                      <div className="col-md-12 mb-3">
                        <label className="form-label">İçerik</label>
                        <div style={{ minHeight: '200px', marginBottom: '80px', position: 'relative', zIndex: 1 }}>
                          <ReactQuill
                            theme="snow"
                            value={formData.content}
                            onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                            style={{ height: '200px' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.content_type === 'image' && (
                    <div className="row mb-4">
                      <div className="col-md-12 mb-3">
                        <label className="form-label fw-bold">Görsel Yükleme</label>
                        <div className="border rounded p-3 bg-light">
                        <div className="d-flex gap-2 align-items-start flex-wrap">
                          <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                            <input
                              type="file"
                              className="form-control"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              onChange={handleImageUpload}
                              id="image-upload"
                            />
                            <small className="text-muted d-block mt-1">Maksimum 10MB. Desteklenen formatlar: JPEG, PNG, GIF, WebP</small>
                          </div>
                          {(isEdit && id) && (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleImageUploadClick}
                              disabled={uploadingImage || !selectedImageFile}
                            >
                              {uploadingImage ? 'Yükleniyor...' : 'Görsel Yükle'}
                            </button>
                          )}
                          {!isEdit && (
                            <div className="alert alert-info mb-0" style={{ fontSize: '0.875rem', minWidth: '200px' }}>
                              <small>Önce popup'ı kaydedin, sonra görsel yükleyebilirsiniz.</small>
                            </div>
                          )}
                        </div>
                        {formData.image_url && (
                          <div className="mt-3">
                            <img 
                              src={formData.image_url} 
                              alt="Popup görseli" 
                              className="img-thumbnail"
                              style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'contain' }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger mt-2"
                              onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}
                            >
                              Görseli Kaldır
                            </button>
                          </div>
                        )}
                        {!formData.image_url && (
                          <div className="mt-2">
                            <input
                              type="url"
                              className="form-control"
                              value={formData.image_url}
                              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                              placeholder="veya görsel URL'si girin: https://example.com/image.jpg"
                            />
                          </div>
                        )}
                        </div>
                      </div>
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Görsel Linki (Tıklanınca gidilecek URL)</label>
                        <input
                          type="url"
                          className="form-control"
                          value={formData.image_link}
                          onChange={(e) => setFormData(prev => ({ ...prev, image_link: e.target.value }))}
                          placeholder="https://example.com"
                        />
                      </div>
                    </div>
                  )}

                  {formData.content_type === 'video' && (
                    <div className="row mb-4">
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Video URL veya Embed Kodu</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={formData.video_url}
                          onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                          placeholder="Video URL veya iframe embed kodu"
                        />
                      </div>
                    </div>
                  )}

                  {/* Button */}
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Buton Metni</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.button_text}
                        onChange={(e) => setFormData(prev => ({ ...prev, button_text: e.target.value }))}
                        placeholder="Örn: Devam Et"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Buton Linki</label>
                      <input
                        type="url"
                        className="form-control"
                        value={formData.button_link}
                        onChange={(e) => setFormData(prev => ({ ...prev, button_link: e.target.value }))}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="row mb-4" style={{ marginTop: '20px', position: 'relative', zIndex: 0 }}>
                    <div className="col-md-12">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <label className="form-label mb-0">Filtreler</label>
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addFilter('role')}
                          >
                            + Rol
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addFilter('plan')}
                          >
                            + Paket
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addFilter('category')}
                          >
                            + Kategori
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addFilter('city')}
                          >
                            + Şehir
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => addFilter('company_type')}
                          >
                            + Şirket Türü
                          </button>
                        </div>
                      </div>
                      {filters.length > 0 && (
                        <div className="alert alert-info">
                          {filters.some(f => f.filter_value) ? (
                            <>Bu filtrelerle <strong>{filterCount}</strong> kişiye gösterilecek</>
                          ) : (
                            <>Filtre seçiniz</>
                          )}
                        </div>
                      )}
                      {filters.map((filter, index) => (
                        <div key={index} className="card mb-2">
                          <div className="card-body p-3">
                            <div className="row align-items-center">
                              <div className="col-md-4">
                                <select
                                  className="form-select form-select-sm"
                                  value={filter.filter_type || 'role'}
                                  onChange={(e) => {
                                    updateFilter(index, 'filter_type', e.target.value);
                                    // Tip değiştiğinde value'yu temizle
                                    updateFilter(index, 'filter_value', '');
                                  }}
                                >
                                  <option value="role">Rol</option>
                                  <option value="plan">Paket</option>
                                  <option value="category">Kategori</option>
                                  <option value="city">Şehir</option>
                                  <option value="company_type">Şirket Türü</option>
                                </select>
                              </div>
                              <div className="col-md-6">
                                <select
                                  className="form-select form-select-sm"
                                  value={filter.filter_value || ''}
                                  onChange={(e) => {
                                    updateFilter(index, 'filter_value', e.target.value);
                                  }}
                                >
                                  <option value="">Seçiniz...</option>
                                  {getFilterValueOptions(filter.filter_type || 'role').map((option) => {
                                    // Value'yu doğru şekilde al
                                    const optionValue = option.value || option.id?.toString() || option.name;
                                    const optionLabel = option.label || option.name || optionValue;
                                    return (
                                      <option
                                        key={optionValue}
                                        value={optionValue}
                                      >
                                        {optionLabel} {option.count !== undefined ? `(${option.count})` : ''}
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                              <div className="col-md-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeFilter(index)}
                                >
                                  Sil
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="row mb-4">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Sıralama</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.sort_order}
                        onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                        min="0"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.is_active === 1}
                          onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                        />
                        <label className="form-check-label">Aktif</label>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={formData.cookie_control === 1}
                          onChange={(e) => setFormData(prev => ({ ...prev, cookie_control: e.target.checked ? 1 : 0 }))}
                        />
                        <label className="form-check-label">Çerez Kontrolü (Kapat ve Bir daha gösterme)</label>
                      </div>
                    </div>
                  </div>

                  {/* Statistics (only in edit mode) */}
                  {isEdit && (
                    <div className="row mb-4">
                      <div className="col-md-12">
                        <div className="card">
                          <div className="card-header">
                            <h6 className="mb-0">İstatistikler</h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-3 mb-3">
                                <div className="text-center p-3 bg-primary bg-opacity-10 rounded">
                                  <div className="h4 mb-0 text-primary">{statistics.view || 0}</div>
                                  <small className="text-muted">👁️ Görüntüleme</small>
                                </div>
                              </div>
                              <div className="col-md-3 mb-3">
                                <div className="text-center p-3 bg-success bg-opacity-10 rounded">
                                  <div className="h4 mb-0 text-success">{statistics.click || 0}</div>
                                  <small className="text-muted">👆 Tıklama</small>
                                </div>
                              </div>
                              <div className="col-md-3 mb-3">
                                <div className="text-center p-3 bg-warning bg-opacity-10 rounded">
                                  <div className="h4 mb-0 text-warning">{statistics.close || 0}</div>
                                  <small className="text-muted">❌ Kapatma</small>
                                </div>
                              </div>
                              <div className="col-md-3 mb-3">
                                <div className="text-center p-3 bg-danger bg-opacity-10 rounded">
                                  <div className="h4 mb-0 text-danger">{statistics.dismiss || 0}</div>
                                  <small className="text-muted">🚫 Bir Daha Gösterme</small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit */}
                  <div className="row">
                    <div className="col-md-12">
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? 'Kaydediliyor...' : (isEdit ? 'Güncelle' : 'Oluştur')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate('/popups')}
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default PopupAddPage;

